import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { BankFilesType } from '@nxsys/types/models/bankFilesReports/bankfiles';
import { Option } from '@nxsys/types/models/Common';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { addWorkingDays, isWorkingDay } from '@nxsys/utils/workingDays';
import { Form, Typography } from 'antd';
import { FormProps } from 'antd/lib';
import { toNumber } from 'lodash';
import moment, { Moment } from 'moment';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

type GeneratePaymentFileModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onGenerate: (params: GeneratePaymentFileValues) => Promise<void>;
  data: BankFilesType;
};

export type GeneratePaymentFileValues = {
  paymentProvider: Option<string> | null;
  paymentDate: Moment;
};

const GeneratePaymentFileModal: React.FC<GeneratePaymentFileModalProps> = ({
  isOpen,
  toggle,
  onGenerate,
  data,
}: GeneratePaymentFileModalProps) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // NX-5537: HSBC BACS 18 only. Bacs settles on working days, so the payment
  // (entry) date needs at least 2 working days' lead time, at most 30 days
  // ahead, and must itself be a working day (weekends + England & Wales bank
  // holidays are not Bacs settlement days). Other bank formats keep the previous
  // "today or later" rule.
  const paymentProvider = Form.useWatch('paymentProvider', form) as
    | { label?: string; value?: string }
    | undefined;
  const isHsbcBacs18 = !!paymentProvider?.label
    ?.toLowerCase()
    .includes('hsbc bacs 18');
  const minPaymentDate = useMemo(
    () => (isHsbcBacs18 ? addWorkingDays(moment(), 2) : moment()),
    [isHsbcBacs18],
  );
  const maxPaymentDate = useMemo(() => moment().add(30, 'day'), []);

  const onFinish: FormProps['onFinish'] = (values) => {
    setIsSubmitting(true);
    onGenerate(values)
      .then(() => {
        toggle();
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={messages['common.generatePaymentFile']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.generate'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Form.Item
          name="paymentProvider"
          label={messages['common.paymentProvider']}
          rules={[
            {
              required: true,
              message: messages['common.pleaseSelect'],
            },
          ]}
        >
          <AppSelect
            placeholder={messages['common.select']}
            options={
              data?.paymentCompany?.banksList?.map((bank) => ({
                label: bank.bankName,
                value: bank.id.toString(),
              })) ?? []
            }
          />
        </Form.Item>

        <Form.Item
          name="paymentDate"
          label={messages['common.paymentDate']}
          rules={
            isHsbcBacs18
              ? [
                  {
                    validator: (_rule, value: Moment) =>
                      value && value.isBefore(minPaymentDate, 'day')
                        ? Promise.reject(
                            new Error(messages['common.bacsMinWorkingDaysError']),
                          )
                        : Promise.resolve(),
                  },
                ]
              : undefined
          }
        >
          <AppDatePicker
            placeholder={messages['common.select']}
            format="DD/MM/YYYY"
            minDate={minPaymentDate}
            maxDate={isHsbcBacs18 ? maxPaymentDate : undefined}
            disabledDate={
              isHsbcBacs18 ? (d: Moment) => !isWorkingDay(d) : undefined
            }
          />
        </Form.Item>

        <Typography.Text>{messages['common.bankFileAmount']}</Typography.Text>
        <AppInput
          className="mt-2"
          value={formatMoney(
            toNumber(data.totalAmount),
            showCurrencyByCountry(payrollCountry),
          )}
          disabled
        />
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default GeneratePaymentFileModal;
